/**
 * Created by codex on 2025-04-17.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useState, useEffect, useCallback } from 'react';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import styled from '@emotion/styled';
import { Container, Row, Col } from 'reactstrap';
import { PageResult } from '../../Model/PageResult';
import { ExploreEntry, ExploreEntryApi } from '../../Model/Explore/ExploreEntry';
import { ExploreEntryBuilder } from '../../Model/Explore/ExploreEntryBuilder';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { ExploreCardEntryRenderer } from '../../Components/ExploreEntryCard/ExploreCardEntryRenderer';
import { ExploreCategorySwitch } from '../../Components/ExploreCards/ExploreCategorySwitch';
import { Button } from '../../Components/Button';
import { createPageResult } from '../../Model/PageResult';
import { PageResultApi } from '../../Model/PageResult';
import { getAllExploreCategoriesV3, childCategoriesOf } from '../../Model/Explore/exploreCategoryTreeV3';
import { isPublished } from '../../Utils/isPublished';

const Header = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  line-height: 3rem;
`;

const Description = styled.p`
  margin-top: 16px;
`;

export type ExploreTabLibraryScreenProps = {
  category: ExploreCategory;
};

export const ExploreTabLibraryScreen: React.FC<ExploreTabLibraryScreenProps> = observer(
  ({ category }) => {
    const { t } = useTrans();
    const categoryName = useLocalized(category.name);
    const shortDescription = useLocalized(category.shortDescription);
    const longDescription = useLocalized(category.description);
    const categoryDescription = shortDescription || longDescription;

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(createPageResult<ExploreEntry>());
    const [page, setPage] = useState(0);
    const [childCategories, setChildCategories] = useState<ExploreCategory[]>([]);
    const [childrenLoading, setChildrenLoading] = useState(true);

    useEffect(() => {
      setChildrenLoading(true);
      getAllExploreCategoriesV3()
        .then((all) => setChildCategories(childCategoriesOf(all, category.id).filter(isPublished)))
        .catch(() => setChildCategories([]))
        .finally(() => setChildrenLoading(false));
    }, [category.id]);

    useEffect(() => {
      if (page === 0) {
        setResult(createPageResult());
      }
      setLoading(true);
      PageResultApi.execute(
        ExploreEntryBuilder.find({
          rootCategories: [category.identifier, category.id],
          size: 16,
          page,
        }),
        ExploreEntryApi.count({
          rootCategories: [category.identifier, category.id],
        }),
        page,
        16,
      )
        .then((res) => setResult((prev) => prev.merge(res)))
        .catch(() => setResult(createPageResult()))
        .finally(() => setLoading(false));
    }, [page, category]);

    const handleLoadMore = useCallback(() => setPage((p) => p + 1), []);

    return (
      <Container>
        <Row>
          <Col xs={12}>
            <Header>{categoryName}</Header>
          </Col>
          <Col md={7}>
            <Description>
              {categoryDescription}
            </Description>
          </Col>
        </Row>
        {childCategories.map((child, index) => (
          <ExploreCategorySwitch
            key={child.id}
            category={child}
            categoryIndex={index}
            isLast={index === childCategories.length - 1}
            linkPrefix="/explore-tab/"
          />
        ))}
        <Row>
          {result.content.map((entry) => (
            <ExploreCardEntryRenderer
              key={entry.id}
              entry={entry}
              linkPrefix={`/explore-tab/${category.identifier}/`}
            />
          ))}
        </Row>
        {!loading && !childrenLoading && result.content.length === 0 && childCategories.length === 0 && (
          <Row>
            <Col style={{ textAlign: 'center', padding: '48px 24px' }}>
              {t('exploreCategory.noContent', { defaultValue: 'No content available yet.' })}
            </Col>
          </Row>
        )}
        {!result.last && (
          <Row>
            <Col style={{ textAlign: 'center', paddingTop: 32 }}>
              <Button color="link" disabled={loading} onClick={handleLoadMore}>
                {t('globals.loadMore', { defaultValue: 'Load More' })}
              </Button>
            </Col>
          </Row>
        )}
      </Container>
    );
  },
);